/**
 * @file `ContactFormIsland` — the terminal-framed compose form on
 *       `/contact`.
 * @module @labcraft/www/ui/islands/contact-form
 *
 * The one genuinely stateful surface on the site: field state +
 * validation, the PoW CAPTCHA lifecycle, submit → success-view swap.
 * Moved out of the page component (ADR-026) so `/contact` renders
 * statically like every other route and this island hydrates the form
 * in place. Submit goes through the shared typed client
 * ({@link IslandRuntime}); on any failure the form re-enables and
 * shows a generic recovery message.
 *
 * The static render (and the client's first paint) is the empty
 * compose form — inputs are live immediately after hydration; the
 * submit button additionally gates on the CAPTCHA worker resolving.
 */

import { useMemo, useState, type FormEvent, type JSX } from 'react'
import { CaptchaChallenge } from '../captcha.tsx'
import { TerminalFrame } from '../terminal_frame.tsx'
import { suggestEmailCorrection } from '../email_typos.ts'
import { IslandRuntime } from './runtime.ts'
import {
  CONTACT_SUBJECT_MAX,
  type CaptchaSolution,
} from '../../shared/routes.ts'
import styles from '../contact.module.css'

const MAX_MSG = 4000
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

// Per-field error DOM ids. Inputs reference these via `aria-describedby`
// when an error is rendered, so screen-reader / keyboard users can navigate
// from the offending field to its error text without scanning the DOM.
const ERROR_IDS = {
  name: 'cx-name-err',
  email: 'cx-email-err',
  subject: 'cx-subject-err',
  message: 'cx-msg-err',
} as const

/** The accepted submission echoed into the success view. */
interface Submission {
  name: string
  email: string
  subject: string
}

/** JSON-safe props for the island (none — the form is self-contained). */
export type ContactFormIslandProps = Record<string, never>

/**
 * The contact island: terminal frame + compose form, swapping to the
 * success view after an accepted submission.
 *
 * @returns The terminal-framed form element.
 */
export function ContactFormIsland(_props: ContactFormIslandProps): JSX.Element {
  const [submission, setSubmission] = useState<Submission | null>(null)

  return (
    <TerminalFrame title="labcraft.dev:~/contact">
      <div className={styles.termBody}>
        <span className={styles.termCmd}>
          <span className={styles.prompt}>$</span>{' '}
          <span className={styles.who}>you</span>
          <span className={styles.args}> compose</span>
        </span>
        <span className={styles.termBreak} aria-hidden />

        {submission ? (
          <SuccessView
            submission={submission}
            onSendAnother={() => setSubmission(null)}
          />
        ) : (
          <ComposeForm onSuccess={setSubmission} />
        )}
      </div>
    </TerminalFrame>
  )
}

////////////////////////////////////////////////////////////////////////////////
// Compose form
////////////////////////////////////////////////////////////////////////////////

/** Props for {@link ComposeForm}. */
interface ComposeFormProps {
  onSuccess: (submission: Submission) => void
}

/** Per-field validation error copy. */
type FieldErrors = Partial<
  Record<'name' | 'email' | 'subject' | 'message', string>
>

/**
 * The compose form — field state, client-side validation, CAPTCHA
 * gate, and the typed submit.
 *
 * @param props - See {@link ComposeFormProps}.
 * @returns The form element.
 */
function ComposeForm(props: ComposeFormProps): JSX.Element {
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [subject, setSubject] = useState('')
  const [message, setMessage] = useState('')
  const [errors, setErrors] = useState<FieldErrors>({})
  const [submitErr, setSubmitErr] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [captcha, setCaptcha] = useState<CaptchaSolution | null>(null)

  // Recomputed each render — cheap (curated short domain list), and
  // avoids a stale suggestion when the user accepts one and keeps typing.
  const emailSuggestion = useMemo(
    () => suggestEmailCorrection(email.trim()),
    [email],
  )

  function validate(): boolean {
    const e: FieldErrors = {}
    if (!name.trim()) e.name = 'Required.'
    if (!email.trim()) e.email = 'Required.'
    else if (!EMAIL_REGEX.test(email.trim())) {
      e.email = "That doesn't look like an email."
    }
    if (!subject.trim()) e.subject = 'Required.'
    else if (subject.length > CONTACT_SUBJECT_MAX) {
      e.subject = `Max ${CONTACT_SUBJECT_MAX} characters.`
    }
    if (!message.trim()) e.message = 'Required.'
    else if (message.length > MAX_MSG) {
      e.message = `Max ${MAX_MSG} characters.`
    }
    setErrors(e)
    return Object.keys(e).length === 0
  }

  async function onSubmit(ev: FormEvent<HTMLFormElement>): Promise<void> {
    ev.preventDefault()
    setSubmitErr(null)
    if (!validate()) return
    if (!captcha) {
      // Defense in depth — the submit button is also gated on
      // `captcha != null`, so this only fires if the keyboard activates
      // submit before the worker resolves.
      setSubmitErr('Please wait for the verification check to finish.')
      return
    }
    setSubmitting(true)
    try {
      await IslandRuntime.www().contact.submit({
        name: name.trim(),
        email: email.trim(),
        subject: subject.trim(),
        message,
        captcha,
      })
      props.onSuccess({ name, email, subject: subject.trim() })
    } catch (_) {
      setSubmitErr(
        'Something went wrong on our end. Please try again — or if it keeps happening, ping us on the LabCraft Discord.',
      )
      setSubmitting(false)
    }
  }

  const charCount = message.length
  const overLimit = charCount > MAX_MSG
  const charCountClass = overLimit ? styles.charcountOver : undefined

  return (
    <form className={styles.form} onSubmit={onSubmit} noValidate>
      <div className={styles.row}>
        <label className={styles.rowLabel} htmlFor="cx-name">
          name<span className={styles.req}>:</span>
        </label>
        <div>
          <input
            id="cx-name"
            className={styles.input}
            placeholder="Ada Lovelace"
            value={name}
            onChange={e => setName(e.target.value)}
            disabled={submitting}
            autoComplete="name"
            aria-invalid={errors.name ? true : undefined}
            aria-describedby={errors.name ? ERROR_IDS.name : undefined}
          />
          {errors.name && (
            <div
              id={ERROR_IDS.name}
              className={styles.error}
              role="alert"
              aria-live="polite">
              // {errors.name}
            </div>
          )}
        </div>
      </div>

      <div className={styles.row}>
        <label className={styles.rowLabel} htmlFor="cx-email">
          email<span className={styles.req}>:</span>
        </label>
        <div>
          <input
            id="cx-email"
            type="email"
            className={styles.input}
            placeholder="ada@example.com"
            value={email}
            onChange={e => setEmail(e.target.value)}
            disabled={submitting}
            autoComplete="email"
            aria-invalid={errors.email ? true : undefined}
            aria-describedby={errors.email ? ERROR_IDS.email : undefined}
          />
          {errors.email && (
            <div
              id={ERROR_IDS.email}
              className={styles.error}
              role="alert"
              aria-live="polite">
              // {errors.email}
            </div>
          )}
          {emailSuggestion && !errors.email && (
            <div className={styles.hint}>
              {'// did you mean '}
              <button
                type="button"
                className={styles.hintAccept}
                onClick={() => setEmail(emailSuggestion)}
                disabled={submitting}
                aria-label={`Use suggested email ${emailSuggestion}`}>
                {emailSuggestion}
              </button>
              ?
            </div>
          )}
        </div>
      </div>

      <div className={styles.row}>
        <label className={styles.rowLabel} htmlFor="cx-subject">
          subject<span className={styles.req}>:</span>
        </label>
        <div>
          <input
            id="cx-subject"
            className={styles.input}
            placeholder="Reaching out about…"
            value={subject}
            onChange={e => setSubject(e.target.value)}
            disabled={submitting}
            maxLength={CONTACT_SUBJECT_MAX + 32}
            aria-invalid={errors.subject ? true : undefined}
            aria-describedby={errors.subject ? ERROR_IDS.subject : undefined}
          />
          {errors.subject && (
            <div
              id={ERROR_IDS.subject}
              className={styles.error}
              role="alert"
              aria-live="polite">
              // {errors.subject}
            </div>
          )}
        </div>
      </div>

      <div className={styles.row}>
        <label className={styles.rowLabel} htmlFor="cx-msg">
          message<span className={styles.req}>:</span>
        </label>
        <div>
          <textarea
            id="cx-msg"
            className={styles.textarea}
            placeholder="Tell us what's on your mind. Code blocks and shell output welcome — we read them."
            value={message}
            onChange={e => setMessage(e.target.value)}
            disabled={submitting}
            rows={6}
            aria-invalid={errors.message ? true : undefined}
            aria-describedby={errors.message ? ERROR_IDS.message : undefined}
          />
          {errors.message && (
            <div
              id={ERROR_IDS.message}
              className={styles.error}
              role="alert"
              aria-live="polite">
              // {errors.message}
            </div>
          )}
        </div>
      </div>

      <CaptchaChallenge onSolved={setCaptcha} />

      <div className={styles.actions}>
        <span className={styles.charcount}>
          <span className={charCountClass}>{charCount}</span>
          <span className={styles.charcountLim}> / {MAX_MSG}</span>
        </span>
        <button
          type="submit"
          className={styles.submit}
          disabled={submitting || !captcha}>
          {submitting ? (
            <>
              <span className={styles.submitSpin} aria-hidden />
              Sending…
            </>
          ) : (
            <>Send →</>
          )}
        </button>
      </div>

      {submitErr && (
        <div className={styles.submitError} role="alert">
          // {submitErr}
        </div>
      )}
    </form>
  )
}

////////////////////////////////////////////////////////////////////////////////
// Success view
////////////////////////////////////////////////////////////////////////////////

/** Props for {@link SuccessView}. */
interface SuccessViewProps {
  submission: Submission
  onSendAnother: () => void
}

/**
 * The post-submit success state inside the same terminal frame.
 *
 * @param props - See {@link SuccessViewProps}.
 * @returns The success element.
 */
function SuccessView(props: SuccessViewProps): JSX.Element {
  const { submission } = props
  return (
    <div className={styles.success}>
      <span className={styles.successOutput}>
        <span className={styles.ok}>✓</span> message received from{' '}
        <strong className={styles.successName}>{submission.name}</strong> &lt;
        {submission.email}&gt;
      </span>
      <span className={styles.successOutput}>
        <span className={styles.successLabel}>subject:</span>{' '}
        {submission.subject}
      </span>
      <span className={styles.termBreak} aria-hidden />
      <button
        type="button"
        className={styles.successAgain}
        onClick={props.onSendAnother}>
        ← send another
      </button>
    </div>
  )
}
